import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TrusteesType,
  UpdateTrusteesType
} from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { Trustee } from 'service/dto/trustee.dto';

interface ITrusteeProps {
  updateTrusteesType: UpdateTrusteesType;
  trusteeId: number;
}

export const useCreateTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (trusteesType: TrusteesType): Promise<void> => {
      return services.trusteeService.createTrustee(trusteesType);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateTrustee = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ trusteeId, updateTrusteesType }: ITrusteeProps): Promise<void> => {
      return services.trusteeService.updateTrustee(
        trusteeId,
        updateTrusteesType
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['update-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetTrusteeList = ({
  enabled = true,
  search = undefined
}: {
  search?: string;
  enabled?: boolean;
} = {}) => {
  const searchQuery = search && search?.length >= 3 ? search : '';
  return useQuery(
    ['trustees'],
    () => services.trusteeService.getTrustees(searchQuery),
    {
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeleteTrustee = () =>
  useMutation(
    (trusteeId: number) => services.trusteeService.deleteTrustee(trusteeId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useGetTrustee = ({ trusteeId }: { trusteeId: number }) => {
  return useQuery<Trustee, AxiosError<Error>>(
    ['get_trustee', trusteeId],
    () => services.trusteeService.getTrustee(trusteeId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!trusteeId
    }
  );
};

export const useGetTrusteeDetails = ({ trusteeId }: { trusteeId: number }) => {
  return useQuery<Trustee, AxiosError<Error>>(
    ['get_trustee_details', trusteeId],
    () => services.trusteeService.getTrusteeDetails(trusteeId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!trusteeId
    }
  );
};
